import { useState, useRef, useEffect } from 'react'
import type React from 'react'
import {
  ArrayInput,
  FormDataConsumer,
  ReferenceInput,
  AutocompleteInput,
  SimpleFormIterator,
} from 'react-admin'
import { useChoicesContext } from 'ra-core'

// Wrap AutocompleteInput to reflect the actual loading state from ReferenceInput
const LoadingAutocompleteInput: React.FC<
  React.ComponentProps<typeof AutocompleteInput>
> = (props) => {
  const { isLoading } = useChoicesContext()
  const noOptions = isLoading
    ? 'Loading...'
    : props.noOptionsText || 'No options'
  return (
    <AutocompleteInput
      {...props}
      isLoading={isLoading as any}
      loadingText="Loading..."
      noOptionsText={noOptions}
    />
  )
}

const TagsInput = ({ source }) => {
  // Control when dropdowns are opened to lazily fetch choices
  const [categoryOpen, setCategoryOpen] = useState(false)
  const [tagOpenMap, setTagOpenMap] = useState<Record<string, boolean>>({})

  // Infinite scroll: track perPage dynamically
  const PAGE_SIZE = 50
  const [categoryPerPage, setCategoryPerPage] = useState(PAGE_SIZE)
  const [tagPerPageMap, setTagPerPageMap] = useState<Record<string, number>>({})

  // Debounced incrementers to avoid multiple fetches during scroll (timeout-based, no deps)
  const categoryScrollTimeout = useRef<number | null>(null)
  const scheduleCategoryInc = () => {
    if (categoryScrollTimeout.current) {
      clearTimeout(categoryScrollTimeout.current)
    }
    categoryScrollTimeout.current = window.setTimeout(() => {
      setCategoryPerPage((prev) => prev + PAGE_SIZE)
    }, 250)
  }

  const tagScrollTimeoutsRef = useRef<Record<string, number | null>>({})
  const scheduleTagInc = (rowKey: string) => {
    const existing = tagScrollTimeoutsRef.current[rowKey]
    if (existing) {
      clearTimeout(existing)
    }
    tagScrollTimeoutsRef.current[rowKey] = window.setTimeout(() => {
      setTagPerPageMap((prev) => ({
        ...prev,
        [rowKey]: (prev[rowKey] || PAGE_SIZE) + PAGE_SIZE,
      }))
    }, 250)
  }

  useEffect(() => {
    // Snapshot current timeout ids to avoid referencing changing refs in cleanup
    const categoryTimeout = categoryScrollTimeout.current
    const tagTimeoutsSnapshot = { ...tagScrollTimeoutsRef.current }
    return () => {
      if (categoryTimeout) clearTimeout(categoryTimeout)
      Object.values(tagTimeoutsSnapshot).forEach((id) => {
        if (id) clearTimeout(id)
      })
    }
  }, [])

  const handleCategoryScroll = (event: React.UIEvent<HTMLElement>) => {
    const list = event.currentTarget
    if (list.scrollTop + list.clientHeight >= list.scrollHeight - 48) {
      scheduleCategoryInc()
    }
  }

  return (
    <ArrayInput source={source}>
      <SimpleFormIterator inline>
        <ReferenceInput
          source="tag_category.id"
          reference="tag_categories"
          sort={{ field: 'name', order: 'ASC' }}
          perPage={categoryPerPage}
          page={1}
          // Avoid fetching the full list until the user opens the select
          enableGetChoices={() => categoryOpen}
        >
          <LoadingAutocompleteInput
            label="Tag category"
            optionText="name"
            optionValue="id"
            fullWidth
            openOnFocus
            onFocus={() => setCategoryOpen(true)}
            filterToQuery={(searchText) => ({ q: searchText })}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            loadingText="Loading..."
            noOptionsText="No options"
            ListboxProps={{ onScroll: handleCategoryScroll }}
          />
        </ReferenceInput>
        <FormDataConsumer>
          {({
            scopedFormData, // The data for this item of the ArrayInput
            getSource = () => {
              return ''
            }, // A function to get the valid source inside an ArrayInput
          }) => {
            if (
              !scopedFormData ||
              !scopedFormData.tag_category ||
              !scopedFormData.tag_category ||
              !scopedFormData.tag_category.id
            )
              return null

            // Use the field path as a stable key for this row's dropdown state
            const rowKey = getSource('id') || ''
            const isOpen = tagOpenMap[rowKey]

            const tagPerPage = tagPerPageMap[rowKey] || PAGE_SIZE
            const handleTagScroll = (event: React.UIEvent<HTMLElement>) => {
              const list = event.currentTarget
              if (
                list.scrollTop + list.clientHeight >=
                list.scrollHeight - 48
              ) {
                scheduleTagInc(rowKey)
              }
            }

            return (
              <ReferenceInput
                reference="tags"
                source={getSource('id')}
                filter={{
                  tag_category_id: scopedFormData.tag_category.id,
                }}
                // Do not fetch the (potentially huge) list until the user opens the select
                enableGetChoices={() => isOpen}
                perPage={tagPerPage}
                sort={{ field: 'name', order: 'ASC' }}
              >
                <LoadingAutocompleteInput
                  label="Tag name"
                  optionText="name"
                  optionValue="id"
                  fullWidth
                  openOnFocus
                  onFocus={() =>
                    setTagOpenMap((prev) => ({ ...prev, [rowKey]: true }))
                  }
                  filterToQuery={(searchText) => ({ q: searchText })}
                  isOptionEqualToValue={(option, value) =>
                    option?.id === value?.id
                  }
                  loadingText="Loading..."
                  noOptionsText="No options"
                  ListboxProps={{ onScroll: handleTagScroll }}
                />
              </ReferenceInput>
            )
          }}
        </FormDataConsumer>
      </SimpleFormIterator>
    </ArrayInput>
  )
}

export default TagsInput
